/**
 * Workers Stats Tab
 * Per-worker (extension user) monitoring and analytics
 */

import { useState } from 'react';
import { Row, Col, Card, Table, Select, Empty, Alert, Skeleton, Tag, Segmented, Space, Tooltip as AntTooltip } from 'antd';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useWorkerStats } from '@/hooks/useProjectStats';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { CHART_COLORS } from '@/styles/theme';
import { formatNumber } from '@/utils/formatters';
import { ActivityHeatmap } from './ActivityHeatmap';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const COLORS = [CHART_COLORS.primary, CHART_COLORS.success, CHART_COLORS.warning, CHART_COLORS.purple, CHART_COLORS.cyan, CHART_COLORS.magenta, CHART_COLORS.orange, CHART_COLORS.error];

type DailyMetric = 'clicks' | 'hours' | 'places';
type WeeklyMetric = 'clicks' | 'uniquePlaces' | 'activeDays';

export function WorkersTab() {
  const { data, isLoading, isError, error } = useWorkerStats();
  const isDark = usePreferencesStore((s) => s.theme) === 'dark';
  const [selectedWorker, setSelectedWorker] = useState<string | null>(null);
  const [dailyMetric, setDailyMetric] = useState<DailyMetric>('clicks');
  const [dailyRange, setDailyRange] = useState<7 | 30 | 60>(60);
  const [weeklyMetric, setWeeklyMetric] = useState<WeeklyMetric>('clicks');

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[1, 2, 3].map((i) => <Card key={i} size="small"><Skeleton active paragraph={{ rows: 4 }} /></Card>)}
    </div>
  );
  if (isError) return <Alert type="error" message="Failed to load worker stats" description={(error as Error)?.message} showIcon />;
  if (!data) return <Empty description="Extension database not connected" />;

  const gridColor = isDark ? '#303030' : '#f0f0f0';
  const textColor = isDark ? '#8c8c8c' : '#595959';
  const bgColor = isDark ? '#1f1f1f' : '#fff';
  const tooltipStyle = { backgroundColor: bgColor, border: `1px solid ${gridColor}`, borderRadius: 8 };

  // Render a timestamp as a relative-time Tag with color coding
  const renderTimestamp = (val: string | null | undefined, isOnline?: boolean) => {
    if (isOnline) return <Tag color={CHART_COLORS.success}>Online</Tag>;
    if (!val) return <Tag>Never</Tag>;
    const hours = (Date.now() - new Date(val).getTime()) / 3600000;
    const color = hours < 24 ? CHART_COLORS.success
                : hours < 168 ? CHART_COLORS.warning
                : CHART_COLORS.error;
    return (
      <AntTooltip title={dayjs(val).format('YYYY-MM-DD HH:mm:ss')}>
        <Tag color={color}>{dayjs(val).fromNow()}</Tag>
      </AntTooltip>
    );
  };

  // Worker summary table columns
  const workerColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', fixed: 'left' as const, width: 120 },
    { title: 'Total Clicks', dataIndex: 'totalClicks', key: 'totalClicks', width: 110, align: 'right' as const, sorter: (a: any, b: any) => a.totalClicks - b.totalClicks, render: (v: number) => formatNumber(v) },
    { title: 'Active Days', dataIndex: 'activeDays', key: 'activeDays', width: 100, align: 'right' as const, sorter: (a: any, b: any) => a.activeDays - b.activeDays },
    { title: 'Unique Places', dataIndex: 'uniquePlaces', key: 'uniquePlaces', width: 120, align: 'right' as const, render: (v: number) => formatNumber(v) },
    { title: 'Clicks/Day', dataIndex: 'clicksPerDay', key: 'clicksPerDay', width: 100, align: 'right' as const, sorter: (a: any, b: any) => a.clicksPerDay - b.clicksPerDay },
    { title: 'Avg Hrs/Day', dataIndex: 'avgHoursPerDay', key: 'avgHoursPerDay', width: 110, align: 'right' as const, sorter: (a: any, b: any) => (a.avgHoursPerDay || 0) - (b.avgHoursPerDay || 0), render: (v: number | undefined) => v != null ? `${v.toFixed(1)}h` : '-' },
    { title: 'Repeat Ratio', dataIndex: 'repeatRatio', key: 'repeatRatio', width: 110, align: 'right' as const, render: (v: number) => `${v}x` },
    { title: 'Tenure (days)', dataIndex: 'tenure', key: 'tenure', width: 120, align: 'right' as const },
    {
      title: 'Last Click', dataIndex: 'lastClick', key: 'lastClick', width: 120,
      sorter: (a: any, b: any) => {
        if (!a.lastClick && !b.lastClick) return 0;
        if (!a.lastClick) return 1;
        if (!b.lastClick) return -1;
        return a.lastClick.localeCompare(b.lastClick);
      },
      render: (val: string) => renderTimestamp(val),
    },
    {
      title: 'Extension', key: 'extension', width: 120,
      sorter: (a: any, b: any) => {
        if (a.isOnline && b.isOnline) return 0;
        if (a.isOnline) return -1;
        if (b.isOnline) return 1;
        const aTime = a.lastSeen || a.lastLogin;
        const bTime = b.lastSeen || b.lastLogin;
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        return aTime.localeCompare(bTime);
      },
      render: (_val: unknown, record: any) => renderTimestamp(record.lastSeen || record.lastLogin, record.isOnline),
    },
    {
      title: 'Website', dataIndex: 'lastMapsLogin', key: 'lastMapsLogin', width: 120,
      sorter: (a: any, b: any) => {
        if (!a.lastMapsLogin && !b.lastMapsLogin) return 0;
        if (!a.lastMapsLogin) return 1;
        if (!b.lastMapsLogin) return -1;
        return a.lastMapsLogin.localeCompare(b.lastMapsLogin);
      },
      render: (val: string) => renderTimestamp(val),
    },
  ];

  // Slice daily activity data by time range
  const slicedDates = data.dailyActivity.dates.slice(-dailyRange);
  const sliceOffset = data.dailyActivity.dates.length - dailyRange;

  // Build daily activity chart data
  const dailyChartData = slicedDates.map((date, i) => {
    const point: Record<string, any> = { date };
    data.dailyActivity.users.forEach((u) => {
      const arr = u[dailyMetric];
      point[u.name] = arr[sliceOffset + i] || 0;
    });
    return point;
  });

  // Build weekly trends chart data
  const weeklyChartData = data.weeklyTrends.weeks.map((week, i) => {
    const point: Record<string, any> = { week };
    data.weeklyTrends.users.forEach((u) => {
      point[u.name] = u[weeklyMetric][i] || 0;
    });
    return point;
  });

  const metricLabel = { clicks: 'Clicks', hours: 'Hours', places: 'Places' }[dailyMetric];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Worker Summary Table */}
      <Card title="Worker Summary" size="small">
        <Table
          dataSource={data.workers}
          rowKey="userId"
          size="small"
          pagination={false}
          scroll={{ x: 1350 }}
          columns={workerColumns}
        />
      </Card>

      {/* Daily Activity - All Workers */}
      <Card
        title={`Daily Activity — ${metricLabel}`}
        size="small"
        extra={
          <Space>
            <Segmented
              size="small"
              value={dailyMetric}
              onChange={(v) => setDailyMetric(v as DailyMetric)}
              options={[
                { label: 'Clicks', value: 'clicks' },
                { label: 'Hours', value: 'hours' },
                { label: 'Places', value: 'places' },
              ]}
            />
            <Segmented
              size="small"
              value={dailyRange}
              onChange={(v) => setDailyRange(v as 7 | 30 | 60)}
              options={[
                { label: '7d', value: 7 },
                { label: '30d', value: 30 },
                { label: '60d', value: 60 },
              ]}
            />
          </Space>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} label={{ value: metricLabel, angle: -90, position: 'insideLeft', fill: textColor, fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {data.dailyActivity.users.map((u, i) => (
              <Line key={u.name} type="monotone" dataKey={u.name} stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Weekly Trends */}
      {data.weeklyTrends.weeks.length > 0 && (
        <Card
          title="Weekly Trends (8 weeks)"
          size="small"
          extra={
            <Segmented
              size="small"
              value={weeklyMetric}
              onChange={(v) => setWeeklyMetric(v as WeeklyMetric)}
              options={[
                { label: 'Clicks', value: 'clicks' },
                { label: 'Unique Places', value: 'uniquePlaces' },
                { label: 'Active Days', value: 'activeDays' },
              ]}
            />
          }
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={weeklyChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="week" tick={{ fill: textColor, fontSize: 11 }} />
              <YAxis tick={{ fill: textColor, fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {data.weeklyTrends.users.map((u, i) => (
                <Line key={u.name} type="monotone" dataKey={u.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Per-Worker Detail */}
      <Card
        title="Worker Detail"
        size="small"
        extra={
          <Select
            placeholder="Select a worker"
            style={{ width: 200 }}
            value={selectedWorker}
            onChange={setSelectedWorker}
            allowClear
            options={data.workers.map((w) => ({ label: w.name, value: w.userId }))}
          />
        }
      >
        {selectedWorker ? (() => {
          const worker = data.workers.find((w) => w.userId === selectedWorker);
          const domains = data.domainDistribution.find((d) => d.name === worker?.name);
          const coverage = data.queryCoverage.find((q) => q.userId === selectedWorker);
          const workerHeatmap = data.heatmaps.find((h) => h.userId === selectedWorker);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Row gutter={16}>
                {/* Domain Distribution */}
                <Col xs={24} lg={12}>
                  <Card title="Top Domains" size="small" type="inner">
                    <Table
                      dataSource={domains?.topDomains || []}
                      rowKey="domain"
                      size="small"
                      pagination={false}
                      columns={[
                        { title: 'Domain', dataIndex: 'domain', key: 'domain', ellipsis: true },
                        { title: 'Clicks', dataIndex: 'count', key: 'count', width: 80, align: 'right' as const, render: (v: number) => formatNumber(v) },
                      ]}
                    />
                  </Card>
                </Col>

                {/* Query Coverage */}
                <Col xs={24} lg={12}>
                  <Card title="Search Query Coverage" size="small" type="inner">
                    <Table
                      dataSource={coverage?.queries || []}
                      rowKey="searchQuery"
                      size="small"
                      pagination={false}
                      scroll={{ y: 300 }}
                      columns={[
                        { title: 'Query', dataIndex: 'searchQuery', key: 'searchQuery', ellipsis: true },
                        { title: 'Reviewed', dataIndex: 'reviewed', key: 'reviewed', width: 80, align: 'right' as const },
                        { title: 'Total', dataIndex: 'total', key: 'total', width: 70, align: 'right' as const },
                        {
                          title: '%', dataIndex: 'pct', key: 'pct', width: 60, align: 'right' as const,
                          render: (v: number) => <span style={{ color: v >= 50 ? CHART_COLORS.success : v >= 20 ? CHART_COLORS.warning : CHART_COLORS.error }}>{v}%</span>,
                        },
                      ]}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Per-Worker Activity Heatmap */}
              {workerHeatmap && workerHeatmap.data.length > 0 && (
                <Card title={`${worker?.name} — Activity Heatmap`} size="small" type="inner">
                  <ActivityHeatmap data={workerHeatmap.data} color="purple" isDark={isDark} />
                </Card>
              )}
            </div>
          );
        })() : (
          <Empty description="Select a worker to view details" />
        )}
      </Card>
    </div>
  );
}
